if (!customElements.get('product-slider')) {
  const GALLERY_CURSOR_RADIUS = 32;
  const GALLERY_CURSOR_EASE = 0.15;
  const GALLERY_CURSOR_FRAME_MS = 1000 / 60;
  const SLIDE_DURATION = 400;

  class ProductSlider extends HTMLElement {
    constructor() {
      super();
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.isDesktop = window.innerWidth >= 1100;
      this.currentSlide = 1;
      this.totalSlides = 0;
      this.lastHeaderHeight = null;
      this.lightbox = null;
      this.galleryCursor = null;
      this.galleryCursorFrame = null;
      this.galleryCursorPoint = { x: 0, y: 0 };
      this.galleryCursorTarget = { x: 0, y: 0 };
      this.galleryCursorTime = 0;
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    connectedCallback() {
      this.setupVariantFilter();
      this.init();
      this.setupResizeListener();
      this.watchHeaderHeight();
      this.setupLightbox();
      this.setupGalleryCursor();
    }

    init() {
      this.isDesktop = window.innerWidth >= 1100;
      this.totalSlides = this.querySelectorAll('.product-slider__mobile .swiper-slide').length;
      
      if (this.isDesktop) {
        this.initDesktopSlider();
      } else {
        this.initMobileSlider();
      }

      this.updateHeaderHeight();
    }

    initDesktopSlider() {
      const thumbsContainer = this.querySelector('.product-slider__desktop .product-slider__thumbs');
      const mainContainer = this.querySelector('.product-slider__desktop .product-slider__main');
      
      if (!thumbsContainer || !mainContainer) {
        console.error('Desktop slider containers not found');
        return;
      }

      // Initialize thumbs slider first
      this.thumbsSwiper = new Swiper(thumbsContainer, {
        direction: 'vertical',
        speed: SLIDE_DURATION,
        slidesPerView: 5,
        spaceBetween: 8,
        centeredSlides: true,
        centeredSlidesBounds: true,
      });

      // Initialize main slider with thumbs connection
      this.mainSwiper = new Swiper(mainContainer, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: SLIDE_DURATION,
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        // Main drives the thumbs; the bounded centering clamps the thumbs index, so syncing back would snap main to the wrong slide
        navigation: {
          nextEl: this.querySelector('.thumbs-button-next'),
          prevEl: this.querySelector('.thumbs-button-prev'),
        },
        thumbs: {
          swiper: this.thumbsSwiper
        },
        on: {
          slideChange: () => {
            if (this.thumbsSwiper) {
              this.thumbsSwiper.slideTo(this.mainSwiper.activeIndex);
            }
            this.updateSlideCounter();
          }
        }
      });
    }

    initMobileSlider() {
      const mainContainer = this.querySelector('.product-slider__mobile .product-slider__main');
      
      if (!mainContainer) {
        console.error('Mobile slider container not found');
        return;
      }

      this.mainSwiper = new Swiper(mainContainer, {
        slidesPerView: 1,
        spaceBetween: 10,
        speed: SLIDE_DURATION,
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev'),
        },
        on: {
          slideChange: () => {
            this.updateSlideCounter();
          }
        }
      });

      this.updateSlideCounter();
    }

    updateSlideCounter() {
      const counter = this.querySelector('.slide-counter');
      if (counter) {
        this.currentSlide = this.mainSwiper.activeIndex + 1;
        const currentFormatted = this.currentSlide.toString().padStart(2, '0');
        const totalFormatted = this.totalSlides.toString().padStart(2, '0');
        counter.innerHTML = `<span class="slide-counter__current">${currentFormatted}</span> <span class="slide-counter__sep">/</span> <span class="slide-counter__total">${totalFormatted}</span>`;
      }
    }

    // Media alt text can end in "@Variant title"; mirrors the rule in product-slider.liquid
    setupVariantFilter() {
      const keys = this.querySelector('[data-variant-keys]');

      if (!keys) return;

      this.variantKeys = JSON.parse(keys.textContent);
      this.variantId = this.dataset.variantId;
      this.slideSets = Array.from(
        this.querySelectorAll('.product-slider__mobile .swiper-wrapper, .product-slider__desktop .swiper-wrapper'),
        (wrapper) => ({ wrapper, slides: Array.from(wrapper.children) })
      );

      // Liquid pre-filters for first paint; Swiper counts every child, so hidden ones must leave the DOM
      this.slideSets.forEach(({ wrapper, slides }) => {
        slides.forEach((slide) => slide.removeAttribute('data-filtered'));
        wrapper.replaceChildren(...slides.filter(this.getVariantFilter(this.variantId)));
      });

      this.section = this.closest('section');
      this.onVariantInputChange = (event) => {
        if (!event.target.matches('input[name="id"]')) return;
        this.filterByVariant(event.target.value);
      };
      if (this.section) this.section.addEventListener('change', this.onVariantInputChange);
    }

    getVariantFilter(variantId) {
      const key = this.variantKeys[variantId];
      const slides = this.slideSets[0].slides;
      const isShown = (slide) => !slide.dataset.variant || slide.dataset.variant === key;

      // A variant left with nothing to show falls back to every image
      return slides.some(isShown) ? isShown : () => true;
    }

    filterByVariant(variantId) {
      if (!this.slideSets || String(variantId) === String(this.variantId)) return;
      if (!(variantId in this.variantKeys)) return;

      this.variantId = String(variantId);
      const isShown = this.getVariantFilter(this.variantId);

      this.destroySliders();
      this.slideSets.forEach(({ wrapper, slides }) => wrapper.replaceChildren(...slides.filter(isShown)));
      this.init();
      this.updateSlideCounter();

      if (this.lightbox) {
        customElements.whenDefined('product-lightbox').then(() => this.lightbox.filterSlides(isShown));
      }
    }

    setupLightbox() {
      this.lightbox = this.querySelector('product-lightbox') || document.querySelector('product-lightbox');

      if (!this.lightbox) return;

      this.onTriggerClick = (event) => {
        const trigger = event.target.closest('[data-lightbox-trigger]');

        if (!trigger) return;

        // Swiper only preventDefaults a post-drag click, so check its own flag too
        if (event.defaultPrevented) return;
        if (this.mainSwiper && this.mainSwiper.allowClick === false) return;

        event.preventDefault();

        // Filtering reorders slides, so the position is read off the DOM, not baked into markup
        const slide = trigger.closest('.swiper-slide');
        const index = slide ? Array.prototype.indexOf.call(slide.parentElement.children, slide) : 0;

        this.openLightbox(Math.max(index, 0), trigger);
      };

      this.onLightboxClose = (event) => {
        const index = event.detail.index;

        if (!this.mainSwiper) return;

        this.mainSwiper.slideTo(index, 0);
        if (this.thumbsSwiper) this.thumbsSwiper.slideTo(index, 0);
        this.updateSlideCounter();
      };

      this.addEventListener('click', this.onTriggerClick);
      this.lightbox.addEventListener('product-lightbox:close', this.onLightboxClose);
    }

    setupGalleryCursor() {
      this.galleryCursor = this.querySelector('.product-slider__cursor');

      if (!this.galleryCursor) return;

      // Fixed positioning must not depend on an ancestor transform
      document.body.appendChild(this.galleryCursor);

      this.onGalleryCursorMove = (event) => {
        if (!this.isDesktop) return;

        if (!event.target.closest('.product-slider__desktop .product-slider__zoom')) {
          this.hideGalleryCursor();
          return;
        }

        this.galleryCursorTarget = { x: event.clientX, y: event.clientY };

        // Snap on reveal so the circle can't scale up at the previous spot, then let the
        // easing loop trail the pointer from there
        if (!this.galleryCursor.classList.contains('is-visible')) {
          this.galleryCursorPoint = { ...this.galleryCursorTarget };
          this.placeGalleryCursor();
          this.galleryCursor.classList.add('is-visible');
        }

        this.startGalleryCursorLoop();
      };

      this.onGalleryCursorLeave = () => this.hideGalleryCursor();

      this.addEventListener('mousemove', this.onGalleryCursorMove);
      this.addEventListener('mouseleave', this.onGalleryCursorLeave);
    }

    startGalleryCursorLoop() {
      if (this.galleryCursorFrame) return;

      if (this.prefersReducedMotion) {
        this.galleryCursorPoint = { ...this.galleryCursorTarget };
        this.placeGalleryCursor();
        return;
      }

      this.galleryCursorTime = performance.now();

      const step = (now) => {
        const target = this.galleryCursorTarget;
        const point = this.galleryCursorPoint;

        // Frame-rate independent form of closing GALLERY_CURSOR_EASE of the gap per 60fps frame
        const frames = (now - this.galleryCursorTime) / GALLERY_CURSOR_FRAME_MS;
        const ease = 1 - Math.pow(1 - GALLERY_CURSOR_EASE, frames);
        this.galleryCursorTime = now;

        point.x += (target.x - point.x) * ease;
        point.y += (target.y - point.y) * ease;

        // Park on the pointer instead of easing forever over the last fraction of a pixel
        if (Math.abs(target.x - point.x) < 0.1 && Math.abs(target.y - point.y) < 0.1) {
          this.galleryCursorPoint = { ...target };
          this.placeGalleryCursor();
          this.galleryCursorFrame = null;
          return;
        }

        this.placeGalleryCursor();
        this.galleryCursorFrame = requestAnimationFrame(step);
      };

      this.galleryCursorFrame = requestAnimationFrame(step);
    }

    placeGalleryCursor() {
      const { x, y } = this.galleryCursorPoint;
      this.galleryCursor.style.left = `${x - GALLERY_CURSOR_RADIUS}px`;
      this.galleryCursor.style.top = `${y - GALLERY_CURSOR_RADIUS}px`;
    }

    hideGalleryCursor() {
      if (this.galleryCursorFrame) {
        cancelAnimationFrame(this.galleryCursorFrame);
        this.galleryCursorFrame = null;
      }
      if (this.galleryCursor) this.galleryCursor.classList.remove('is-visible');
    }

    openLightbox(index, trigger) {
      if (!this.lightbox) return;

      this.hideGalleryCursor();

      if (typeof this.lightbox.open === 'function') {
        this.lightbox.open(index, trigger);
        return;
      }

      customElements.whenDefined('product-lightbox').then(() => this.lightbox.open(index, trigger));
    }

    setupResizeListener() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const wasDesktop = this.isDesktop;
          this.isDesktop = window.innerWidth >= 1100;
          
          if (wasDesktop !== this.isDesktop) {
            this.destroySliders();
            this.init();
          }
        }, 250);
      });
    }

    getHeaderSections() {
      return [
        document.querySelector('#shopify-section-header'),
        document.querySelector('#shopify-section-announcement-bar')
      ].filter(Boolean);
    }

    updateHeaderHeight() {
      const totalHeight = this.getHeaderSections().reduce((height, section) => height + section.offsetHeight, 0);

      // Set the CSS variable on the document root
      if (totalHeight !== this.lastHeaderHeight) {
        this.lastHeaderHeight = totalHeight;
        document.documentElement.style.setProperty('--header-total-height', `${totalHeight}px`);
      }

      this.updateHeaderVisibleHeight();

      return totalHeight;
    }

    // The announcement bar scrolls away while the header sticks, so the space the
    // header takes at the top of the viewport shrinks as the page scrolls
    updateHeaderVisibleHeight() {
      const bottom = this.getHeaderSections().reduce(
        (lowest, section) => Math.max(lowest, section.getBoundingClientRect().bottom),
        0
      );
      const visibleHeight = Math.round(Math.max(0, bottom));

      if (visibleHeight !== this.lastHeaderVisibleHeight) {
        this.lastHeaderVisibleHeight = visibleHeight;
        document.documentElement.style.setProperty('--header-visible-height', `${visibleHeight}px`);
      }
    }

    watchHeaderHeight() {
      // The custom overrides and swiper stylesheets load deferred (media="print" onload),
      // so on an uncached load the header is measured while still partly unstyled and
      // reports a much taller height than it ends up with. That inflated value is baked
      // into --header-total-height and shrinks the gallery for the rest of the page life.
      // Re-measure whenever the header actually changes height.
      const sections = this.getHeaderSections();

      if (sections.length && 'ResizeObserver' in window) {
        this.headerObserver = new ResizeObserver(() => this.updateHeaderHeight());
        sections.forEach((section) => this.headerObserver.observe(section));
      }

      // A page opened in a background tab lays out and runs its observers while hidden,
      // so re-measure once it is actually shown and painted.
      this.remeasureHeader = () => requestAnimationFrame(() => this.updateHeaderHeight());

      this.onWindowLoad = this.remeasureHeader;
      window.addEventListener('load', this.onWindowLoad);

      this.onPageShow = this.remeasureHeader;

      this.onHeaderScroll = () => {
        if (this.headerScrollFrame) return;

        this.headerScrollFrame = requestAnimationFrame(() => {
          this.headerScrollFrame = null;
          this.updateHeaderVisibleHeight();
        });
      };
      window.addEventListener('scroll', this.onHeaderScroll, { passive: true });
      window.addEventListener('pageshow', this.onPageShow);

      this.onVisibilityChange = () => {
        if (!document.hidden) this.remeasureHeader();
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (document.fonts) {
        document.fonts.ready.then(() => this.remeasureHeader());
      }
    }

    destroySliders() {
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }
      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }
    }

    disconnectedCallback() {
      this.destroySliders();

      if (this.section && this.onVariantInputChange) {
        this.section.removeEventListener('change', this.onVariantInputChange);
        this.onVariantInputChange = null;
      }

      if (this.onTriggerClick) {
        this.removeEventListener('click', this.onTriggerClick);
        this.onTriggerClick = null;
      }
      if (this.lightbox && this.onLightboxClose) {
        this.lightbox.removeEventListener('product-lightbox:close', this.onLightboxClose);
        this.onLightboxClose = null;
      }
      if (this.onGalleryCursorMove) {
        this.removeEventListener('mousemove', this.onGalleryCursorMove);
        this.removeEventListener('mouseleave', this.onGalleryCursorLeave);
        this.onGalleryCursorMove = null;
        this.onGalleryCursorLeave = null;
      }
      this.hideGalleryCursor();
      if (this.galleryCursor && this.galleryCursor.parentElement === document.body) {
        this.galleryCursor.remove();
        this.galleryCursor = null;
      }

      if (this.headerObserver) {
        this.headerObserver.disconnect();
        this.headerObserver = null;
      }
      if (this.onWindowLoad) {
        window.removeEventListener('load', this.onWindowLoad);
        this.onWindowLoad = null;
      }
      if (this.onPageShow) {
        window.removeEventListener('pageshow', this.onPageShow);
        this.onPageShow = null;
      }
      if (this.onHeaderScroll) {
        window.removeEventListener('scroll', this.onHeaderScroll);
        if (this.headerScrollFrame) cancelAnimationFrame(this.headerScrollFrame);
        this.headerScrollFrame = null;
        this.onHeaderScroll = null;
      }
      if (this.onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.onVisibilityChange = null;
      }
    }
  }

  customElements.define('product-slider', ProductSlider);
}